import { useEffect, useState } from "react";
import { Alert, Col, Fade, Form, InputGroup, Modal, Row } from "react-bootstrap";
import DefaultButton from "../../../../../components/buttons/default-button/DefaultButton";
import { useApolloClient } from "@apollo/client/react";
import { gql } from '@apollo/client'

const VALIDATE_TOTP_QUERY = gql`
    query ValidateTOTP($userId: Int!, $totpCode: String!) {
        validateTOTP(userId: $userId, totpCode: $totpCode) {
            success
            errorMessage
        }
    }
`

interface validateTotpResult {
    validateTOTP: {
        success: boolean
        errorMessage: string | null
    }
}

interface qrModalProps {
    visible: boolean
    onHide: () => void
    userId: number
    username: string
    totpSecretDataUrl: string
}

export default function QrModal({ visible, onHide, userId, username, totpSecretDataUrl }: qrModalProps) {
    const [ validated, setValidated ] = useState(false)
    const [ totpCode, setTotpCode ] = useState("")
    const [ showError, setShowError ] = useState(false)
    const [ errorMessage, setErrorMessage ] = useState("")
    const [ showSuccess, setShowSuccess ] = useState(false)
    const client = useApolloClient()
    
    const defaultErrorMessage = "Hiba a szerverrel való kommunikáció közben"

    useEffect(() => {
        setValidated(false)
        setTotpCode('')
        setShowError(false)
        setErrorMessage('')
        setShowSuccess(false)
    }, [ visible ])
    
    const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault()

        setValidated(true)

        // Reset result variables
        setShowError(false)
        setErrorMessage("")
        setShowSuccess(false)

        if(totpCode.length !== 6) {
            setShowError(true)
            setErrorMessage("TOTP kód hossza 6 karakter kell, hogy legyen!")

            return
        }

        try {
            const data = await client.query<validateTotpResult>({
                query: VALIDATE_TOTP_QUERY,
                variables: {
                    userId,
                    totpCode
                },
                fetchPolicy: 'no-cache'
            })

            if(!data || !data.data) {
                setShowError(true)
                setErrorMessage(defaultErrorMessage)
                return
            }

            const { success, errorMessage } = data.data.validateTOTP
            if(success) {
                setShowSuccess(true)
            }
            else {
                setShowError(true)
                setErrorMessage(errorMessage ?? defaultErrorMessage)
            }
        }
        catch {
            setShowError(true)
            setErrorMessage(defaultErrorMessage)
        }
    }

    return(
        <Modal
            show={ visible }
            onHide={ () => onHide() }
            fullscreen="md-down"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title style={{textAlign: "center"}}>
                    <span>QR kód</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row className='align-items-center'>
                    <Col sm="3">
                        <span>Felhasználó:</span>
                    </Col>

                    <Col sm="4">
                        <b>{username}</b>
                    </Col>
                
                </Row>

                <Row className='align-items-center'>
                    <Col sm="5">
                        <img style={{ width:"168px", height: "auto" }} src={ totpSecretDataUrl } alt="Scan with Google Authenticator" />
                    </Col>

                    <Col sm="6" className='text-left'>
                        <h4>Ellenőrzés:</h4>

                        <Form id="qr-form" noValidate validated={ validated } onSubmit={ handleSubmit } >
                            <Row className='mb-3 justify-content-center'>
                                <Form.Group as={ Col } md="12" controlId="totp-secret">
                                    <InputGroup hasValidation>
                                        <Form.Control
                                            type="number"
                                            placeholder="TOTP validálás"
                                            required
                                            maxLength={ 6 }
                                            onChange={ e => setTotpCode(e.target.value) }
                                            autoFocus={ true }
                                        />
                            
                                        <Form.Control.Feedback type="invalid">
                                            TOTP kód megadása kötelező
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Row>
                        </Form>
                    </Col>

                    <Row style={{textAlign:"center"}} className='mt-3 justify-content-center'>
                        <Col sm="6">
                            <DefaultButton title={"Kilépés"} onClick={() => onHide()} />
                        </Col>

                        <Col sm="6">
                            <DefaultButton title={"Validálás"} form="qr-form" />
                        </Col>
                    </Row>
                </Row>

                <Row className='mb-3 justify-content-center mt-3'>
                    <Col md="12">
                        <Alert
                            show={ showError }
                            transition={ Fade }
                            variant='danger'
                            onClose={ () => setShowError(false) }
                            dismissible
                            style={{borderRadius: "var(--bs-border-radius)"}}
                        >
                            <b>
                                <span dangerouslySetInnerHTML={{__html: errorMessage }}></span>
                            </b>
                        </Alert>
                    </Col>
                </Row>

                <Row className='mb-3 justify-content-center mt-3'>
                    <Col md="12">
                        <Alert
                            show={ showSuccess }
                            transition={ Fade }
                            variant='success'
                            onClose={ () => setShowSuccess(false) }
                            dismissible
                            style={{borderRadius: "var(--bs-border-radius)"}}
                        >
                            <b>
                                Sikeres validáció
                            </b>
                        </Alert>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    )
}